import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTie } from '@fortawesome/free-solid-svg-icons';
import useSupervisorAnalytics from '../hooks/useSupervisorAnalytics';
import useSupervisorWorkload from '../hooks/useSupervisorWorkload';
import SupervisorFilter from '../components/supervisor-analytics/SupervisorFilter';
import SupervisorSummaryCards from '../components/supervisor-analytics/SupervisorSummaryCards';
import SupervisorLeaderboard from '../components/supervisor-analytics/SupervisorLeaderboard';
import SupervisorActivityTable from '../components/supervisor-analytics/SupervisorActivityTable';
import SupervisorWorkloadTable from '../components/supervisor-analytics/SupervisorWorkloadTable';
import SupervisorWorkloadDetail from '../components/supervisor-analytics/SupervisorWorkloadDetail';
import Pagination from '../components/common/Pagination';
import userService from '../services/userService';
import useAuth from '../hooks/useAuth';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;

const getDefaultDateRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const fmt = (d) => d.toISOString().split('T')[0];
    return { from: fmt(firstDay), to: fmt(lastDay) };
};

const SupervisorAnalyticsPage = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { userRole } = useRole();
    const isSupervisor = userRole === USER_ROLES.DELIVERY_SUPERVISOR;
    const isAdminOrSuper = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
    const isSalesStaff = userRole === USER_ROLES.SALES_STAFF;
    const isOperationsManager = userRole === USER_ROLES.OPERATIONS_MANAGER;

    const tabs = React.useMemo(() => {
        const base = [
            { id: 'summary',     label: t('supervisorAnalytics.tabs.summary') },
            { id: 'loading',     label: t('supervisorAnalytics.tabs.loadingActivity') },
            { id: 'unloading',   label: t('supervisorAnalytics.tabs.unloadingActivity') },
        ];
        if (isSupervisor || isAdminOrSuper || isSalesStaff || isOperationsManager) {
            base.push({ id: 'workload', label: t('supervisorAnalytics.tabs.workload') });
        }
        if (!isSupervisor) {
            base.push({ id: 'leaderboard', label: t('supervisorAnalytics.tabs.leaderboard') });
        }
        return base;
    }, [isSupervisor, isAdminOrSuper, isSalesStaff, isOperationsManager]);

    const {
        summary,
        loadingItems,
        loadingTotal,
        unloadingItems,
        unloadingTotal,
        leaderboard,
        fetching,
        error,
        fetchSummary,
        fetchLoading,
        fetchUnloading,
        fetchLeaderboard,
    } = useSupervisorAnalytics();

    const {
        items: workloadItems,
        total: workloadTotal,
        loading: workloadLoading,
        detail: workloadDetail,
        detailLoading: workloadDetailLoading,
        fetchList: fetchWorkload,
        fetchDetail: fetchWorkloadDetail,
        clearDetail: clearWorkloadDetail,
    } = useSupervisorWorkload();

    const [selectedWorkloadRow, setSelectedWorkloadRow] = useState(null);

    const [supervisors, setSupervisors] = useState([]);
    const [supervisorsLoading, setSupervisorsLoading] = useState(false);
    const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
    const [dateRange, setDateRange] = useState(getDefaultDateRange);
    const [activeTab, setActiveTab] = useState(tabs[0]?.id || 'summary');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(DEFAULT_PAGE_LIMIT);

    // Tracks whether initial load + fetch is done so tab-change effect doesn't double-fire on mount
    const isTabEffectReady = useRef(false);

    // selectedSupervisorId and dateRange ref for use inside callbacks without stale closures
    const filterRef = useRef({ supervisorId: '', dateRange, statusFilter });
    filterRef.current = { supervisorId: selectedSupervisorId, dateRange, statusFilter };

    const buildParams = useCallback((page = 1) => {
        const { supervisorId, dateRange: dr } = filterRef.current;
        const skip = (page - 1) * pageSize;
        const base = { from_date: dr.from, to_date: dr.to };
        if (supervisorId) base.supervisor_id = supervisorId;
        return { base, skip };
    }, [pageSize]);

    const fetchForTab = useCallback(async (tab, page = 1) => {
        const { base, skip } = buildParams(page);
        const { statusFilter: sf } = filterRef.current;
        if (tab === 'summary') {
            await fetchSummary(base);
        } else if (tab === 'loading') {
            const params = { ...base, skip, limit: pageSize };
            if (sf) params.status = sf;
            await fetchLoading(params);
        } else if (tab === 'unloading') {
            const params = { ...base, skip, limit: pageSize };
            if (sf) params.status = sf;
            await fetchUnloading(params);
        } else if (tab === 'leaderboard') {
            await fetchLeaderboard(base);
        } else if (tab === 'workload') {
            if (isSupervisor && user?.id) {
                await fetchWorkloadDetail(user.id);
            } else {
                const wParams = { ...base, skip, limit: pageSize };
                if (sf) wParams.status = sf;
                await fetchWorkload(wParams);
            }
        }
    }, [buildParams, pageSize, fetchSummary, fetchLoading, fetchUnloading, fetchLeaderboard, fetchWorkload, fetchWorkloadDetail, isSupervisor, user]);

    // Load supervisors on mount, default-select first, then do initial fetch — single call
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            setSupervisorsLoading(true);
            try {
                const res = await userService.getDeliverySupervisorsStatus();
                if (cancelled) return;
                const list = res.success ? (Array.isArray(res.data) ? res.data : []) : [];
                setSupervisors(list);

                let initialId = '';
                if (isSupervisor && user?.id) {
                    initialId = user.id;
                } else if (list.length > 0) {
                    initialId = list[0].id;
                }

                if (initialId) {
                    setSelectedSupervisorId(initialId);
                }

                // Initial summary fetch with correct supervisor id
                const dr = getDefaultDateRange();
                const base = { from_date: dr.from, to_date: dr.to };
                if (initialId) base.supervisor_id = initialId;
                await fetchSummary(base);
            } finally {
                if (!cancelled) {
                    setSupervisorsLoading(false);
                    isTabEffectReady.current = true;
                }
            }
        };
        init();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch when tab changes — skip the initial render (handled above)
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Re-fetch on pagination change
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        if (activeTab === 'loading' || activeTab === 'unloading' || activeTab === 'workload') {
            fetchForTab(activeTab, currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleApply = () => {
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
    };

    const activeTotal = activeTab === 'loading' ? loadingTotal : unloadingTotal;
    const activeTotalPages = Math.ceil(activeTotal / pageSize) || 1;

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            {/* Page header */}
            <Box mb={5}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box sx={{
                        bgcolor: '#189AB4',
                        borderRadius: 2,
                        width: 40, height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white',
                    }}>
                        <FontAwesomeIcon icon={faUserTie} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        Supervisor Analytics
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700 }}>
                    Monitor supervisor performance, loading and unloading activity, and team rankings.
                </Typography>
            </Box>

            {/* Filter bar */}
            <SupervisorFilter
                supervisors={supervisors}
                supervisorsLoading={supervisorsLoading}
                selectedSupervisorId={selectedSupervisorId}
                onSupervisorChange={setSelectedSupervisorId}
                fromDate={dateRange.from}
                toDate={dateRange.to}
                onFromDateChange={(val) => setDateRange(prev => ({ ...prev, from: val }))}
                onToDateChange={(val) => setDateRange(prev => ({ ...prev, to: val }))}
                onSubmit={handleApply}
                loading={fetching}
                activeTab={activeTab}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                disableSupervisorSelector={isSupervisor}
            />

            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: '2px solid #f3f4f6', flexWrap: 'wrap' }}>
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            borderBottom: activeTab === tab.id ? '3px solid #189AB4' : '3px solid transparent',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            color: activeTab === tab.id ? '#189AB4' : '#6b7280',
                            transition: 'all 0.15s',
                            marginBottom: -2,
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </Box>

            {/* Error */}
            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {/* Initial loading spinner */}
            {fetching && activeTab === 'summary' && !summary && (
                <Box display="flex" justifyContent="center" p={10}>
                    <CircularProgress size={40} thickness={4} sx={{ color: '#189AB4' }} />
                </Box>
            )}

            {activeTab === 'summary' && (
                <Fade in={!!summary}>
                    <div><SupervisorSummaryCards summary={summary} /></div>
                </Fade>
            )}

            {activeTab === 'loading' && (
                <>
                    <SupervisorActivityTable type="loading" items={loadingItems} loading={fetching} />
                    {loadingItems?.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={activeTotalPages}
                            totalCount={activeTotal}
                            onPageChange={setCurrentPage}
                            loading={fetching}
                            totalLabel={t('supervisorAnalytics.pagination.totalLabels.loadingRecords')}
                            pageSize={pageSize}
                        />
                    )}
                </>
            )}

            {activeTab === 'unloading' && (
                <>
                    <SupervisorActivityTable type="unloading" items={unloadingItems} loading={fetching} />
                    {unloadingItems?.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={activeTotalPages}
                            totalCount={activeTotal}
                            onPageChange={setCurrentPage}
                            loading={fetching}
                            totalLabel={t('supervisorAnalytics.pagination.totalLabels.unloadingRecords')}
                            pageSize={pageSize}
                        />
                    )}
                </>
            )}

            {activeTab === 'leaderboard' && (
                <Fade in>
                    <div>
                        {fetching && !leaderboard?.length && (
                            <Box display="flex" justifyContent="center" p={10}>
                                <CircularProgress size={40} thickness={4} sx={{ color: '#189AB4' }} />
                            </Box>
                        )}
                        <SupervisorLeaderboard supervisors={leaderboard} />
                    </div>
                </Fade>
            )}

            {activeTab === 'workload' && (
                isSupervisor ? (
                    <SupervisorWorkloadDetail
                        row={workloadDetail ?? {}}
                        detail={workloadDetail}
                        loading={workloadDetailLoading}
                        inline
                        onClose={() => {}}
                    />
                ) : (
                    <>
                        <SupervisorWorkloadTable
                            items={workloadItems}
                            loading={workloadLoading}
                            onSelectSupervisor={(row) => {
                                setSelectedWorkloadRow(row);
                                fetchWorkloadDetail(row.delivery_supervisor_id);
                            }}
                        />
                        {workloadTotal > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(workloadTotal / pageSize) || 1}
                                totalCount={workloadTotal}
                                onPageChange={setCurrentPage}
                                loading={workloadLoading}
                                totalLabel={t('supervisorAnalytics.pagination.totalLabels.supervisors')}
                                pageSize={pageSize}
                            />
                        )}
                    </>
                )
            )}

            {selectedWorkloadRow && (
                <SupervisorWorkloadDetail
                    row={selectedWorkloadRow}
                    detail={workloadDetail}
                    loading={workloadDetailLoading}
                    onClose={() => {
                        setSelectedWorkloadRow(null);
                        clearWorkloadDetail();
                    }}
                />
            )}
        </Container>
    );
};

export default SupervisorAnalyticsPage;
